import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons";
import alertIntelligenceAPI from "../services/AlertIntelligenceAPI";
import { useSubscription } from "../context/SubscriptionContext";
import "../css/Common.css";
import "./css/AlertsIntelligenceHub.css";

/**
 * Alert card component for list display.
 * Shows alert summary with threat level, category, and quick actions.
 *
 * @param {Object} alert - Alert data
 * @param {Function} onView - Handler for viewing the alert
 * @param {Function} onMarkReviewed - Handler for marking reviewed
 * @param {Function} onShare - Handler for sharing
 * @param {Function} onDismiss - Handler for dismissing
 */
function AlertCard({ alert, onView, onMarkReviewed, onShare, onDismiss }) {
  const { canShare } = useSubscription();
  const hasShareCapability = canShare();

  if (!alert) return null;

  const threatLabel = alertIntelligenceAPI.getThreatLevelLabel(
    alert.threatLevel,
  );
  const threatColor = alertIntelligenceAPI.getThreatLevelColor(
    alert.threatLevel,
  );

  /**
   * Handle card click (view detail)
   */
  const handleClick = () => {
    onView?.(alert.id);
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  /**
   * Handle action button click (prevent card click)
   */
  const handleAction = (e, action) => {
    e.stopPropagation();
    action?.(alert);
  };

  return (
    <div
      className={`aih-alert-card aih-threat-${threatLabel.toLowerCase()}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Alert: ${alert.title}. Threat level: ${threatLabel}`}
    >
      {/* Left: Threat indicator and icon */}
      <div className="aih-card-left">
        <div
          className="aih-card-threat-bar"
          style={{ backgroundColor: threatColor }}
          aria-hidden="true"
        />
        <span className="aih-card-category-icon">
          {getCategoryIcon(alert.category)}
        </span>
      </div>

      {/* Center: Main content */}
      <div className="aih-card-content">
        {/* Title row */}
        <div className="aih-card-title-row">
          <h3 className="aih-card-title">{alert.title}</h3>

          {/* Badges */}
          <div className="aih-card-badges">
            {/* Threat level badge */}
            <span
              className="aih-card-threat-badge"
              style={{
                backgroundColor: threatColor,
                color: threatLabel === "LOW" ? "#1a1a2e" : "#ffffff",
              }}
            >
              {threatLabel}
            </span>

            {/* Peak activity */}
            {alert.peakActivity && (
              <span className="aih-card-peak-badge" title="Peak Activity">
                ↑
              </span>
            )}

            {/* Has training */}
            {alert.relatedTrainingUuid && (
              <span
                className="aih-card-training-badge"
                title="Training Available"
              >
                T
              </span>
            )}

            {/* Reviewed status */}
            {alert.userReviewed && (
              <span
                className="aih-card-reviewed-badge"
                title="You've reviewed this"
              >
                ✓
              </span>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div className="aih-card-meta">
          <span className="aih-card-category">
            {formatCategory(alert.category)}
          </span>
          <span className="aih-card-separator">•</span>
          <span className="aih-card-time">
            {alertIntelligenceAPI.formatRelativeTime(alert.createdAt)}
          </span>
          {alert.affectedRegions?.length > 0 && (
            <>
              <span className="aih-card-separator">•</span>
              <span className="aih-card-regions">
                {alert.affectedRegions.slice(0, 2).join(", ")}
                {alert.affectedRegions.length > 2 &&
                  ` +${alert.affectedRegions.length - 2}`}
              </span>
            </>
          )}
        </div>

        {/* Description */}
        <p className="aih-card-description">
          {truncateText(alert.description || alert.summary, 150)}
        </p>

        {/* Tags */}
        {alert.tags?.length > 0 && (
          <div className="aih-card-tags">
            {alert.tags.slice(0, 4).map((tag, index) => (
              <span key={index} className="aih-card-tag">
                {tag}
              </span>
            ))}
            {alert.tags.length > 4 && (
              <span className="aih-card-tag aih-more-tags">
                +{alert.tags.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Stats row */}
        <div className="aih-card-stats">
          <span className="aih-card-stat" title="Views">
            {formatNumber(alert.viewCount || 0)} views
          </span>
          <span className="aih-card-stat" title="Shares">
            {formatNumber(alert.shareCount || 0)} shares
          </span>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="aih-card-actions">
        {!alert.userReviewed && onMarkReviewed && (
          <button
            className="aih-card-action aih-action-review"
            onClick={(e) => handleAction(e, () => onMarkReviewed(alert.id))}
            title="Mark as reviewed"
            aria-label="Mark as reviewed"
          >
            ✓
          </button>
        )}

        {onShare && (
          <button
            className={`aih-card-action aih-action-share${!hasShareCapability ? " aih-action-disabled" : ""}`}
            onClick={
              hasShareCapability
                ? (e) => handleAction(e, () => onShare(alert))
                : (e) => e.stopPropagation()
            }
            disabled={!hasShareCapability}
            title={
              hasShareCapability
                ? "Share with family"
                : "Available For Family or Extended Family Plans"
            }
            aria-label={
              hasShareCapability
                ? "Share with family"
                : "Available For Family or Extended Family Plans"
            }
          >
            <FontAwesomeIcon icon={faShareAlt} />
          </button>
        )}

        {onDismiss && (
          <button
            className="aih-card-action aih-action-dismiss"
            onClick={(e) => handleAction(e, () => onDismiss(alert.id))}
            title="Dismiss"
            aria-label="Dismiss"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

function formatCategory(category) {
  if (!category) return "";
  return category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}

function getCategoryIcon(category) {
  // Return first letter of category as a simple indicator
  if (!category) return "•";
  const formatted = formatCategory(category);
  return formatted.charAt(0).toUpperCase();
}

function truncateText(text, maxLength) {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
}

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
}

export default AlertCard;
